// Display helpers shared by every dashboard. Pure — no React, no Firestore.

export function fmtTs(ts: unknown): string {
  if (!ts) return "—";
  const obj = ts as { toDate?: () => Date; seconds?: number; _seconds?: number };
  if (typeof obj.toDate === "function") {
    return obj.toDate().toLocaleString();
  }
  if (typeof obj.seconds === "number") {
    return new Date(obj.seconds * 1000).toLocaleString();
  }
  if (typeof obj._seconds === "number") {
    return new Date(obj._seconds * 1000).toLocaleString();
  }
  if (typeof ts === "string" || typeof ts === "number") {
    const d = new Date(ts);
    if (!isNaN(d.getTime())) return d.toLocaleString();
  }
  return "—";
}

export function fmtDate(ts: unknown): string {
  if (!ts) return "—";
  const obj = ts as { toDate?: () => Date; seconds?: number; _seconds?: number };
  if (typeof obj.toDate === "function") {
    return obj.toDate().toLocaleDateString();
  }
  if (typeof obj.seconds === "number") {
    return new Date(obj.seconds * 1000).toLocaleDateString();
  }
  if (typeof obj._seconds === "number") {
    return new Date(obj._seconds * 1000).toLocaleDateString();
  }
  if (typeof ts === "string" || typeof ts === "number") {
    const d = new Date(ts);
    if (!isNaN(d.getTime())) return d.toLocaleDateString();
  }
  return "—";
}

export function fmtBytes(bytes: number | null | undefined): string {
  if (bytes == null) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export function fmtEur(eur: number | null | undefined): string {
  if (eur == null || !Number.isFinite(eur)) return "—";
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(eur);
}

export function fmtNumber(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("es-ES").format(n);
}

// USD amounts on the observability panel (model pricing is denominated in USD
// upstream). Sub-cent aggregates keep 2 decimals; that's fine for range totals.
export function fmtUsd(usd: number | null | undefined): string {
  if (usd == null || !Number.isFinite(usd)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(usd);
}

// Compact percentage for ratios already in 0..1 (cache hit, error rate).
export function fmtPct(ratio: number | null | undefined, digits = 1): string {
  if (ratio == null || !Number.isFinite(ratio)) return "—";
  return `${(ratio * 100).toFixed(digits)}%`;
}

// UI-only mapping. Backend keeps slugs (`tier-1`, `tier-2`, `enterprise`,
// `trial`) as the source of truth; this is the single place to rename
// them for users without rewriting Firestore docs.
const TIER_DISPLAY_NAMES: Record<string, string> = {
  "tier-1": "Starter",
  "tier-2": "Pro",
  enterprise: "Enterprise",
  trial: "Prueba gratuita",
};

export function tierDisplayName(
  slug: string | null | undefined,
  fallback?: string | null,
): string {
  if (!slug) return fallback ?? "Sin plan";
  return TIER_DISPLAY_NAMES[slug] ?? fallback ?? slug;
}

// Firestore timestamps arrive in several shapes depending on the source
// (admin SDK Timestamp objects, REST JSON `{seconds, nanoseconds}`, raw
// strings/numbers). This collapses them to millis for arithmetic
// (e.g. "días restantes" in the trial banner). Returns null when nothing
// useful can be derived.
export function millisFromTs(v: unknown): number | null {
  if (!v) return null;
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  const obj = v as { toDate?: () => Date; seconds?: number; _seconds?: number };
  if (typeof obj.toDate === "function") return obj.toDate().getTime();
  if (typeof obj.seconds === "number") return obj.seconds * 1000;
  if (typeof obj._seconds === "number") return obj._seconds * 1000;
  return null;
}
